import {useEffect, useState} from 'react';
import {APIError} from 'data/errors';
import {IFormData} from 'presentation/pages/public/forgot-password/components/Forms/Step1/schema';
import {formatApiErrorResponse} from 'presentation/utils/formatApiErrorResponse';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useHostName} from 'presentation/hooks';
import {getLoginIdentifier} from 'presentation/utils';
import {HttpStatusCode} from 'data/protocols/http';
import {useSteps} from '../useSteps';
import {PublicFlowEnum} from '../../constants/PublicFlowEnum';
import {UseForgotPasswordProps} from './types';
import {LOCAL_STORAGE_KEYS} from '../../forgot-password/constants/localStorageKeys';
import {sendForgotPasswordToken} from '../../services/sendForgotPasswordToken';
import {useForgotPasswordData} from '../useForgotPasswordData';
import {useResendTimer} from '../useResendTimer';

const {serverError} = translations['pt-br'].commons;
const {invalidUserOrPassword} = translations['pt-br'].publicFlow.APIErrors;

export function useForgotPasswordRecoverUserInfo({methods}: UseForgotPasswordProps) {
	const {
		getValues,
		formState: {isValidating},
	} = methods;

	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const {
		dataHostNameState: {productCode},
	} = useHostName();
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const [errorStatusCode, setErrorStatusCode] = useState<number | null>(null);
	const [showNoContactModal, setShowNoContactModal] = useState(false);
	const formFieldValues = getValues();

	const {handleNextStep} = useSteps(PublicFlowEnum.FORGOT_PASSWORD);
	const {setForgotPasswordData} = useForgotPasswordData();
	const {resetTimer} = useResendTimer();
	const isDirty = Object.values(formFieldValues).every(val => val !== '');
	const showAPIError = requestState === OperationStateTypesEnum.ERROR && isDirty;
	const showConfirmationModal = requestState === OperationStateTypesEnum.DONE;

	const performUserCheck = async ({birthday, login}: IFormData, signal?: AbortSignal) => {
		setRequestState(OperationStateTypesEnum.PENDING);
		const identifier = getLoginIdentifier(login);
		if (identifier === '0') {
			setRequestState(OperationStateTypesEnum.ERROR);
			setErrorStatusCode(HttpStatusCode.unprocessable);
			setApiErrorMsg(invalidUserOrPassword);
			return null;
		}

		const response = await sendForgotPasswordToken({
			cdEmpresaPlano: productCode,
			dtNascimento: birthday,
			id: login,
			tipoId: identifier,
			signal,
		}).catch(() => {
			setRequestState(OperationStateTypesEnum.ERROR);
			setErrorStatusCode(HttpStatusCode.serverError);
			return null;
		});

		return {response, identifier};
	};

	const handleCheckPersonalData = async ({birthday, login}: IFormData) => {
		try {
			const result = await performUserCheck({birthday, login});

			if (!result) {
				return;
			}

			const {response, identifier} = result;

			if (response?.content) {
				if (
					response.content.emailMascarado === null &&
					response.content.telefoneMascarado === null
				) {
					setShowNoContactModal(true);
					return;
				}
				setRequestState(OperationStateTypesEnum.DONE);
				setForgotPasswordData(state => ({
					...state,
					cdEmpresaPlano: productCode,
					dtNascimento: birthday,
					id: login,
					tipoId: identifier,
					emailMascarado: response.content.emailMascarado,
					telefoneMascarado: response.content.telefoneMascarado,
				}));
				resetTimer();
				localStorage.setItem(LOCAL_STORAGE_KEYS.CAME_FROM_SEND_STEP, 'true');
				handleNextStep();
			} else {
				setShowNoContactModal(true);
				setRequestState(OperationStateTypesEnum.DONE);
			}
		} catch (error) {
			setRequestState(OperationStateTypesEnum.ERROR);

			if (error instanceof APIError) {
				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);
				setErrorStatusCode(error.statusCode);

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
		setErrorStatusCode(null);
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		handleCheckPersonalData,
		apiErrorMsg,
		showAPIError,
		showConfirmationModal,
		errorStatusCode,
		clearAPIError,
		showNoContactModal,
		setShowNoContactModal,
	};
}
